'use client';

import { c as _c } from "react/compiler-runtime";
import { createSidebarRegistry, useSidebar } from '@repo/ui/components';
import type { SidebarResize } from '@repo/ui/components';
import { createContext, useContext, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { BREAKPOINT_VALUES } from '../sortable-grid/hooks/breakpoint-values.ts';
const appPanels = createSidebarRegistry({
  rightPanel: {}
});
const rightPanelIds = {
  changeCustomer: 'change-customer',
  changeEndCustomer: 'change-end-customer',
  customizeOverview: 'customize-overview',
  duplicateLoad: 'duplicate-load',
  internalChat: 'internal-chat',
  plannedPayment: 'planned-payment',
  riskVerification: 'risk-verification',
  tableSettings: 'table-settings'
} as const;
type RightPanelContextValue = {
  activePanelId: null | RightPanelId;
  closePanel: (_id: RightPanelId) => void;
  hasBeenOpened: boolean;
  open: boolean;
  openDefaultPanel: (_id: RightPanelId) => boolean;
  openMobile: boolean;
  openPanel: (_id: RightPanelId) => void;
  portalHost: HTMLDivElement | null;
  registerPanel: (_registration: RightPanelRegistration) => () => void;
  registrations: ReadonlyMap<RightPanelId, readonly RightPanelRegistration[]>;
  resize: SidebarResize;
  setPortalHost: (_host: HTMLDivElement | null) => void;
  shellWidth: string;
  togglePanel: (_id: RightPanelId) => void;
  updatePanel: (_registration: RightPanelRegistration) => void;
};
type RightPanelId = (typeof rightPanelIds)[keyof typeof rightPanelIds];
type RightPanelRegistration = {
  contentKey?: number | string;
  id: RightPanelId;
  instanceId: symbol;
  title: ReactNode;
  width?: string;
};
const RightPanelContext = createContext<null | RightPanelContextValue>(null);
const NAVIGATION_YIELD_QUERY = `(min-width: ${BREAKPOINT_VALUES.md}px) and (max-width: ${BREAKPOINT_VALUES['2xl'] - 0.02}px)`;

// Resize limits in pixels: 20rem and 60rem at the default root font size.
const panelWidthLimits = {
  maxWidth: 960,
  minWidth: 320
};
const widthStorageKey = (id: RightPanelId) => `right-panel-width:${id}`;

// Storage can be blocked or full; the provider also keeps resized widths for this page view.
const readSavedWidth = (id: RightPanelId) => {
  try {
    // A missing or unreadable value is 0 or NaN.
    const width = Number(localStorage.getItem(widthStorageKey(id)));
    return width > 0 ? Math.min(panelWidthLimits.maxWidth, Math.max(panelWidthLimits.minWidth, width)) : null;
  } catch {
    return null;
  }
};
const saveWidth = (id: RightPanelId, width: number) => {
  try {
    localStorage.setItem(widthStorageKey(id), String(width));
  } catch {
    // Keep the width for this page view only.
  }
};

// Caps a width saved on a wide screen so it still leaves room for the page on a narrow one.
const toShellWidth = (width: number) => `min(${width}px, 60vw)`;
const getOwner = (registrations: ReadonlyMap<RightPanelId, readonly RightPanelRegistration[]>, id: RightPanelId) => {
  const registrationsForId = registrations.get(id);
  return registrationsForId?.at(-1);
};

/**
 * Coordinates the universal right-hand sidebar. Registered panel children stay in
 * their caller tree and are portaled into the shell only while selected.
 */
const RightPanelProvider = t0 => {
  const $ = _c(49);
  const {
    children
  } = t0;
  const {
    isMobile,
    open,
    openMobile,
    setOpen,
    setOpenMobile
  } = useSidebar(appPanels.rightPanel);
  const navigation = useSidebar();
  const [activePanelId, setActivePanelId] = useState(null);
  const [hasBeenOpened, setHasBeenOpened] = useState(false);
  const [portalHost, setPortalHost] = useState(null);
  const [shellWidth, setShellWidth] = useState("24rem");
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = new Map();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [registrations, setRegistrations] = useState(t1);
  const activePanelIdRef = useRef(null);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = new Map();
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const registrationsRef = useRef(t2);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      open: false,
      openMobile: false
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const sidebarStateRef = useRef(t3);
  const navigationRef = useRef(navigation);
  const collapsedNavigationRef = useRef(false);
  let t4;
  let t5;
  if ($[3] !== activePanelId) {
    t4 = () => {
      activePanelIdRef.current = activePanelId;
    };
    t5 = [activePanelId];
    $[3] = activePanelId;
    $[4] = t4;
    $[5] = t5;
  } else {
    t4 = $[4];
    t5 = $[5];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[6] !== open || $[7] !== openMobile) {
    t6 = () => {
      sidebarStateRef.current = {
        open,
        openMobile
      };
    };
    t7 = [open, openMobile];
    $[6] = open;
    $[7] = openMobile;
    $[8] = t6;
    $[9] = t7;
  } else {
    t6 = $[8];
    t7 = $[9];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[10] !== navigation) {
    t8 = () => {
      navigationRef.current = navigation;
      if (navigation.open) {
        collapsedNavigationRef.current = false;
      }
    };
    t9 = [navigation];
    $[10] = navigation;
    $[11] = t8;
    $[12] = t9;
  } else {
    t8 = $[11];
    t9 = $[12];
  }
  useEffect(t8, t9);
  let t10;
  let t11;
  if ($[13] !== open) {
    t10 = () => {
      const collapseNavigation = () => {
        if (!navigationRef.current.open) {
          return;
        }
        collapsedNavigationRef.current = true;
        navigationRef.current.setOpen(false, {
          persist: false
        });
      };
      const restoreNavigation = () => {
        if (!collapsedNavigationRef.current) {
          return;
        }
        collapsedNavigationRef.current = false;
        navigationRef.current.setOpen(true, {
          persist: false
        });
      };
      if (!open) {
        restoreNavigation();
        return;
      }
      const query = window.matchMedia(NAVIGATION_YIELD_QUERY);
      const handleChange = () => {
        if (query.matches) {
          collapseNavigation();
        } else {
          restoreNavigation();
        }
      };
      handleChange();
      query.addEventListener("change", handleChange);
      return () => {
        query.removeEventListener("change", handleChange);
        restoreNavigation();
      };
    };
    t11 = [open];
    $[13] = open;
    $[14] = t10;
    $[15] = t11;
  } else {
    t10 = $[14];
    t11 = $[15];
  }
  useEffect(t10, t11);
  let t12;
  let t13;
  if ($[16] !== isMobile || $[17] !== open || $[18] !== shellWidth) {
    t12 = () => {
      if (!open || isMobile) {
        return;
      }
      const root = document.documentElement;
      root.style.setProperty("--right-panel-width", shellWidth);
      return () => {
        root.style.removeProperty("--right-panel-width");
      };
    };
    t13 = [isMobile, open, shellWidth];
    $[16] = isMobile;
    $[17] = open;
    $[18] = shellWidth;
    $[19] = t12;
    $[20] = t13;
  } else {
    t12 = $[19];
    t13 = $[20];
  }
  useEffect(t12, t13);
  let t14;
  if ($[21] !== setOpen || $[22] !== setOpenMobile) {
    t14 = () => {
      const resizedWidths = new Map();
      const updateShellWidth = id => {
        const owner = getOwner(registrationsRef.current, id);
        if (!owner) {
          return;
        }
        const savedWidth = resizedWidths.get(id) ?? readSavedWidth(id);
        setShellWidth(savedWidth ? toShellWidth(savedWidth) : owner.width ?? "24rem");
      };
      const closeSidebar = () => {
        sidebarStateRef.current = {
          open: false,
          openMobile: false
        };
        setOpen(false);
        setOpenMobile(false);
      };
      const openSidebar = id_0 => {
        updateShellWidth(id_0);
        activePanelIdRef.current = id_0;
        sidebarStateRef.current = {
          open: true,
          openMobile: true
        };
        setActivePanelId(id_0);
        setHasBeenOpened(true);
        setOpen(true);
        setOpenMobile(true);
      };
      return {
        closePanel: id_1 => {
          if (activePanelIdRef.current === id_1) {
            closeSidebar();
          }
        },
        openDefaultPanel: id_2 => {
          const {
            open: open_0,
            openMobile: openMobile_0
          } = sidebarStateRef.current;
          if (getOwner(registrationsRef.current, id_2) && !open_0 && !openMobile_0) {
            openSidebar(id_2);
            return true;
          }
          return false;
        },
        openPanel: id_3 => {
          if (getOwner(registrationsRef.current, id_3)) {
            openSidebar(id_3);
          }
        },
        registerPanel: registration => {
          const currentRegistrations = registrationsRef.current.get(registration.id) ?? [];
          const nextRegistrations = new Map(registrationsRef.current);
          nextRegistrations.set(registration.id, [...currentRegistrations, registration]);
          registrationsRef.current = nextRegistrations;
          setRegistrations(nextRegistrations);
          if (activePanelIdRef.current === registration.id) {
            updateShellWidth(registration.id);
          }
          return () => {
            const currentRegistrations_0 = registrationsRef.current.get(registration.id) ?? [];
            const nextRegistrationsForId = currentRegistrations_0.filter(currentRegistration => currentRegistration.instanceId !== registration.instanceId);
            const nextRegistrations_0 = new Map(registrationsRef.current);
            if (nextRegistrationsForId.length === 0) {
              nextRegistrations_0.delete(registration.id);
            } else {
              nextRegistrations_0.set(registration.id, nextRegistrationsForId);
            }
            registrationsRef.current = nextRegistrations_0;
            setRegistrations(nextRegistrations_0);
            if (activePanelIdRef.current === registration.id) {
              if (nextRegistrationsForId.length === 0) {
                activePanelIdRef.current = null;
                setActivePanelId(null);
                setHasBeenOpened(false);
                closeSidebar();
              } else {
                updateShellWidth(registration.id);
              }
            }
          };
        },
        resize: {
          ...panelWidthLimits,
          onResize: width => {
            const id_4 = activePanelIdRef.current;
            if (!id_4) {
              return;
            }
            resizedWidths.set(id_4, width);
            saveWidth(id_4, width);
            setShellWidth(toShellWidth(width));
          }
        },
        togglePanel: id_5 => {
          const {
            open: open_1,
            openMobile: openMobile_1
          } = sidebarStateRef.current;
          if (activePanelIdRef.current === id_5 && open_1 && openMobile_1) {
            closeSidebar();
            return;
          }
          if (getOwner(registrationsRef.current, id_5)) {
            openSidebar(id_5);
          }
        },
        updatePanel: registration_0 => {
          const currentRegistrations_1 = registrationsRef.current.get(registration_0.id) ?? [];
          const currentRegistration_1 = currentRegistrations_1.find(currentRegistration_0 => currentRegistration_0.instanceId === registration_0.instanceId);
          if (!currentRegistration_1) {
            return;
          }
          const nextRegistrations_1 = new Map(registrationsRef.current);
          nextRegistrations_1.set(registration_0.id, currentRegistrations_1.map(current => current === currentRegistration_1 ? registration_0 : current));
          registrationsRef.current = nextRegistrations_1;
          setRegistrations(nextRegistrations_1);
          if (activePanelIdRef.current === registration_0.id && getOwner(nextRegistrations_1, registration_0.id)?.instanceId === registration_0.instanceId) {
            updateShellWidth(registration_0.id);
          }
        }
      };
    };
    $[21] = setOpen;
    $[22] = setOpenMobile;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  const [actions] = useState(t14);
  let t15;
  let t16;
  if ($[24] !== activePanelId || $[25] !== open || $[26] !== openMobile || $[27] !== setOpen || $[28] !== setOpenMobile) {
    t15 = () => {
      if (!activePanelId) {
        return;
      }
      if (!open && openMobile) {
        setOpenMobile(false);
      }
      if (open && !openMobile) {
        setOpen(false);
      }
    };
    t16 = [activePanelId, open, openMobile, setOpen, setOpenMobile];
    $[24] = activePanelId;
    $[25] = open;
    $[26] = openMobile;
    $[27] = setOpen;
    $[28] = setOpenMobile;
    $[29] = t15;
    $[30] = t16;
  } else {
    t15 = $[29];
    t16 = $[30];
  }
  useEffect(t15, t16);
  let t17;
  if ($[31] !== actions.closePanel || $[32] !== actions.openDefaultPanel || $[33] !== actions.openPanel || $[34] !== actions.registerPanel || $[35] !== actions.resize || $[36] !== actions.togglePanel || $[37] !== actions.updatePanel || $[38] !== activePanelId || $[39] !== hasBeenOpened || $[40] !== open || $[41] !== openMobile || $[42] !== portalHost || $[43] !== registrations || $[44] !== shellWidth) {
    t17 = {
      activePanelId,
      closePanel: actions.closePanel,
      hasBeenOpened,
      open,
      openDefaultPanel: actions.openDefaultPanel,
      openMobile,
      openPanel: actions.openPanel,
      portalHost,
      registerPanel: actions.registerPanel,
      registrations,
      resize: actions.resize,
      setPortalHost,
      shellWidth,
      togglePanel: actions.togglePanel,
      updatePanel: actions.updatePanel
    };
    $[31] = actions.closePanel;
    $[32] = actions.openDefaultPanel;
    $[33] = actions.openPanel;
    $[34] = actions.registerPanel;
    $[35] = actions.resize;
    $[36] = actions.togglePanel;
    $[37] = actions.updatePanel;
    $[38] = activePanelId;
    $[39] = hasBeenOpened;
    $[40] = open;
    $[41] = openMobile;
    $[42] = portalHost;
    $[43] = registrations;
    $[44] = shellWidth;
    $[45] = t17;
  } else {
    t17 = $[45];
  }
  let t18;
  if ($[46] !== children || $[47] !== t17) {
    t18 = <RightPanelContext.Provider value={t17} data-sentry-element="RightPanelContext.Provider" data-sentry-component="RightPanelProvider" data-sentry-source-file="context.tsx">{children}</RightPanelContext.Provider>;
    $[46] = children;
    $[47] = t17;
    $[48] = t18;
  } else {
    t18 = $[48];
  }
  return t18;
};
const useRightPanelContext = () => {
  const context = useContext(RightPanelContext);
  if (!context) {
    throw new Error("useRightPanelContext must be used within RightPanelProvider.");
  }
  return context;
};
export { appPanels, getOwner, rightPanelIds, RightPanelProvider, useRightPanelContext };
export type { RightPanelId, RightPanelRegistration };